// AppRuntime — the single place where UI meets the workspace, i18n and preferences.
// Modules receive everything through hooks; there are no globals and no window.* patching.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createI18n, localeInfo, type I18n } from '@vero/i18n';
import { getSetting, saveSetting, today as todayFn, addDays, DomainError, effectiveBrand, brandToAdvisor, adoptLegacyAdvisor, type EffectiveBrand, type Changeset, type DatasetView, type Day } from '@vero/domain';
import { syncService } from './sync-service.ts';
import { noteSaveError } from './save-status.ts';
import { errorKey, StorageError, ValidationError, type Command, type Workspace } from '@vero/store';
import { computeAttention, type AttentionItem } from '@vero/intelligence';

export interface AdvisorProfile { name?: string; company?: string; phone?: string; email?: string; website?: string; license?: string; signature?: string; officeAddress?: string; officeMapsUrl?: string }
export type Theme = 'dark' | 'light' | 'auto';

interface Toast { id: number; text: string; kind: 'success' | 'error' | 'info'; action?: { label: string; run: () => void }; ms: number }

export interface Runtime {
  ws: Workspace;
  view: DatasetView;
  i18n: I18n;
  t: I18n['t'];
  today: Day;
  locale: string;
  theme: Theme;
  region: string;
  advisor: AdvisorProfile;
  /** effective advisor/agency identity for client-facing documents (branding profiles, schema v5) */
  brand: EffectiveBrand;
  /** platform user id when signed in, otherwise 'local' */
  userId: string;
  attention: AttentionItem[];
  engine: string;
  demo: boolean;
  run(cmd: Command, okKey?: string, okParams?: Record<string, string | number>, opts?: { undoable?: boolean }): Promise<Changeset | null>;
  undo(): Promise<void>;
  toast(text: string, kind?: Toast['kind'], action?: Toast['action']): void;
  open(node: ReactNode): void;
  close(): void;
  /** Full-screen working layer (Flow). Sits under modals so a form can open on top of it. */
  layer: ReactNode;
  openLayer(node: ReactNode): void;
  closeLayer(): void;
  setPref(key: 'locale' | 'theme' | 'region', value: string): Promise<void>;
  snooze(id: string): Promise<void>;
  snoozeCommand(id: string): Command;
}

const Ctx = createContext<Runtime | null>(null);
export function useRuntime(): Runtime {
  const r = useContext(Ctx);
  if (!r) throw new Error('Runtime missing');
  return r;
}
export const useT = () => useRuntime().t;

export function RuntimeProvider({ ws, engine, children }: { ws: Workspace; engine: string; children: ReactNode }) {
  const view = useSyncExternalStore((cb) => ws.subscribe(() => cb()), () => ws.view);
  // a browser in Greek or Russian opens the CRM in that language (Russian complete since 15 Oct 2026); otherwise English
  const locale = getSetting<string>(view, 'locale', navigator.language?.startsWith('el') ? 'el' : navigator.language?.startsWith('ru') ? 'ru' : 'en');
  const theme = getSetting<Theme>(view, 'theme', 'dark');
  const region = getSetting<string>(view, 'region', 'CY');
  const legacyAdvisor = getSetting<AdvisorProfile>(view, 'advisor', {});
  const userId = syncService()?.snapshot().userId ?? 'local';
  const brand = useMemo(() => effectiveBrand(view, userId, legacyAdvisor as Record<string, string | undefined>), [view, userId, legacyAdvisor]);
  const advisor = useMemo(() => ({ ...legacyAdvisor, ...Object.fromEntries(Object.entries(brandToAdvisor(brand)).filter(([, v]) => v)) }) as AdvisorProfile, [legacyAdvisor, brand]);
  // one-time adoption of the 0.1–0.4 advisor profile into a branding profile (idempotent, never overwrites)
  useEffect(() => { if (!ws.readOnlyReason && !view.rows('branding_profiles').length && view.rows('settings').some((x) => x.key === 'advisor')) void ws.run((v, c) => adoptLegacyAdvisor(v, c, userId)).catch(() => undefined); }, []);
  const demo = getSetting<boolean>(view, 'demo', false);
  const snoozedMap = getSetting<Record<string, string>>(view, 'snoozed', {});
  const [today, setToday] = useState<Day>(todayFn());
  useEffect(() => { const id = setInterval(() => setToday(todayFn()), 60_000); return () => clearInterval(id); }, []);
  useEffect(() => { ws.setRegion(region); }, [ws, region]);

  const i18n = useMemo(() => createI18n({ locale, currency: regionCurrency(region), onMissing: (m) => console.warn('[i18n] missing', m.key) }), [locale, region]);
  const uiJson = JSON.stringify(getSetting<Record<string, boolean>>(view, 'ui', {}));
  useEffect(() => {
    const ui = JSON.parse(uiJson) as Record<string, boolean>;
    const el = document.documentElement;
    if (ui.contrast) el.dataset.contrast = 'high'; else delete el.dataset.contrast;
    if (ui.motion) el.dataset.motion = 'reduce'; else delete el.dataset.motion;
    if (ui.compact) el.dataset.density = 'compact'; else delete el.dataset.density;
  }, [uiJson]);
  useEffect(() => {
    const info = localeInfo(locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = info.dir;
    const apply = () => {
      const eff = theme === 'auto' ? (matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark') : theme;
      document.documentElement.dataset.theme = eff;
    };
    apply();
    if (theme === 'auto') { const mq = matchMedia('(prefers-color-scheme: light)'); mq.addEventListener('change', apply); return () => mq.removeEventListener('change', apply); }
  }, [locale, theme]);

  const snoozed = useMemo(() => new Set(Object.entries(snoozedMap).filter(([, until]) => until > today).map(([id]) => id)), [snoozedMap, today]);
  // Smart alerts are configurable (spec §42): per rule "off" or a fixed severity; defaults otherwise
  const ruleJson = JSON.stringify(getSetting<Record<string, string>>(view, 'rules', {}));
  const ruleCfg = useMemo(() => JSON.parse(ruleJson) as Record<string, string>, [ruleJson]);
  const attention = useMemo(() => {
    const SCORE: Record<string, number> = { critical: 88, high: 68, medium: 48, low: 25 };
    return computeAttention(view, { today, region, snoozed })
      .filter((a) => ruleCfg[a.rule] !== 'off')
      .map((a) => (ruleCfg[a.rule] && SCORE[ruleCfg[a.rule]!] ? { ...a, severity: ruleCfg[a.rule] as AttentionItem['severity'], score: SCORE[ruleCfg[a.rule]!]! } : a))
      .sort((a, b) => b.score - a.score);
  }, [view, today, region, snoozed, ruleCfg]);

  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const toast = useCallback((text: string, kind: Toast['kind'] = 'success', action?: Toast['action']) => {
    const id = ++seq.current;
    const ms = kind === 'error' ? 6000 : action ? 6500 : 3200;
    setToasts((x) => [...x.slice(-3), { id, text, kind, action, ms }]);
    setTimeout(() => setToasts((x) => x.filter((y) => y.id !== id)), ms);
  }, []);
  const [modal, setModal] = useState<ReactNode>(null);
  const [layer, setLayer] = useState<ReactNode>(null);

  const undo = useCallback(async () => {
    try {
      const cs = await ws.undo();
      toast(i18n.t(cs ? 'undo.done' : 'common.nothing_to_undo'), cs ? 'info' : 'info');
    } catch (e) {
      toast(i18n.t(e instanceof StorageError && e.code === 'undo.conflict' ? 'undo.conflict' : errorKey(e)), 'error');
    }
  }, [ws, i18n, toast]);

  const run = useCallback(async (cmd: Command, okKey?: string, okParams?: Record<string, string | number>, opts: { undoable?: boolean } = {}) => {
    try {
      const cs = await ws.run(cmd);
      const undoable = opts.undoable ?? (cs.action !== 'settings.save');
      if (okKey) toast(i18n.t(okKey, okParams), 'success', undoable && ws.canUndo() ? { label: i18n.t('undo.action'), run: () => { void undo(); } } : undefined);
      return cs;
    } catch (e) {
      console.error('[command]', e);
      const key = e instanceof ValidationError ? 'common.error' : errorKey(e);
      toast(i18n.t(key), 'error');
      if (!(e instanceof DomainError)) reportError(e);
      // a failed write (not a business rule or a read-only workspace) shows in the save status
      if (!(e instanceof DomainError) && !(e instanceof ValidationError) && !(e instanceof StorageError && e.code === 'workspace.read_only')) noteSaveError(e);
      return null;
    }
  }, [ws, i18n, toast]);

  const setPref = useCallback(async (key: 'locale' | 'theme' | 'region', value: string) => { await run((v, c) => saveSetting(v, key, value, c)); }, [run]);
  /** The snooze as a command, so callers can fold it into a larger atomic change (Flow). */
  const snoozeCommand = useCallback((id: string): Command => (v, c) => {
    const current = getSetting<Record<string, string>>(v, 'snoozed', {});
    const next = Object.fromEntries(Object.entries(current).filter(([, until]) => until > c.today));
    next[id] = addDays(c.today, 1);
    return saveSetting(v, 'snoozed', next, c);
  }, []);
  const snooze = useCallback(async (id: string) => {
    await run(snoozeCommand(id), 'attention.snoozed', undefined, { undoable: true });
  }, [run, snoozeCommand]);

  const rt: Runtime = {
    ws, view, i18n, t: i18n.t, today, locale, theme, region, advisor, brand, userId, attention, engine, demo, run, undo, toast,
    open: (n) => setModal(n), close: () => setModal(null), setPref, snooze, snoozeCommand,
    layer, openLayer: (n) => setLayer(n), closeLayer: () => setLayer(null),
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modal) { e.stopImmediatePropagation(); setModal(null); return; }
      const el = e.target as HTMLElement | null;
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(el?.tagName ?? '') || !!el?.isContentEditable;
      if (!typing && !modal && (e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') { e.preventDefault(); void undo(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [modal, undo]);

  return (
    <Ctx.Provider value={rt}>
      {children}
      {layer}
      {modal && <div className="overlay" onMouseDown={(e: MouseEvent) => { if (e.target === e.currentTarget) setModal(null); }}>{modal}</div>}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((x) => (
          <div key={x.id} className={`toast ${x.kind}`} data-testid="toast">
            <span className="grow">{x.text}</span>
            {x.action && <button className="btn btn-sm btn-ghost undo-btn" data-testid="toast-action" onClick={() => { x.action!.run(); setToasts((all) => all.filter((y) => y.id !== x.id)); }}>{x.action.label}<span className="kbd" style={{ marginInlineStart: 4 }}>⌘Z</span></button>}
            {x.action && <i className="ttl" style={{ animationDuration: `${x.ms}ms` }} />}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

function regionCurrency(region: string): string {
  return ({ CY: 'EUR', GR: 'EUR', DE: 'EUR', INTL: 'EUR', GB: 'GBP', RO: 'RON', AE: 'AED', IL: 'ILS' } as Record<string, string>)[region] ?? 'EUR';
}

// --- diagnostics: structured error log (kept in memory + sessionStorage, exportable) ---------
export interface ErrorEntry { at: string; message: string; stack?: string; module?: string }
const ERRORS: ErrorEntry[] = [];
export function reportError(e: unknown, module?: string) {
  const err = e instanceof Error ? e : new Error(String(e));
  ERRORS.push({ at: new Date().toISOString(), message: err.message, stack: err.stack?.split('\n').slice(0, 6).join('\n'), module });
  if (ERRORS.length > 200) ERRORS.shift();
}
export const errorLog = () => ERRORS.slice();
window.addEventListener('error', (e) => reportError(e.error ?? e.message, 'window'));
window.addEventListener('unhandledrejection', (e) => reportError(e.reason, 'promise'));
